import React, { useState, useId } from "react";
import {
  ArrowLeft,
  FlaskConical,
  Shield,
  ChevronDown,
  ChevronRight,
  Info,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { formatDateTimeIST } from "../../utils/dateTime.ts";
import rawSnapshot from "../../data/researchSnapshot.json";
import type {
  ResearchReportSnapshot,
  ConfigurationData,
} from "../../types/researchReport.ts";

export interface ResearchReportViewProps {
  onBack: () => void;
  isSyntheticMode?: boolean;
}

const snapshot: ResearchReportSnapshot | null = rawSnapshot as unknown as ResearchReportSnapshot;

/** Formats a decimal metric as percentage with consistent 2 decimal precision. Never turns missing/null into 0%. */
function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return "—";
  }
  return `${(value * 100).toFixed(2)}%`;
}

export const ResearchReportView: React.FC<ResearchReportViewProps> = ({
  onBack,
  isSyntheticMode = false,
}) => {
  const [selectedKey, setSelectedKey] = useState<string>("equal_weight_fusion");
  const [isMethodologyOpen, setIsMethodologyOpen] = useState<boolean>(false);
  const methodologyId = useId();

  if (!snapshot || !snapshot.configurations) {
    return (
      <div
        role="region"
        aria-label="Research evaluation"
        className="flex-grow p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full select-none"
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to overview"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-panel border border-border text-xs font-medium text-foreground hover:bg-panel-hover transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
        >
          <ArrowLeft className="w-3.5 h-3.5 text-foreground-muted" />
          <span>Back to overview</span>
        </button>

        <div className="p-8 rounded-lg bg-panel border border-border text-center space-y-3 max-w-lg mx-auto">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto" />
          <h2 className="text-base font-medium text-foreground">
            Research Evaluation Unavailable
          </h2>
          <p className="text-xs text-foreground-muted">
            The offline comparison snapshot could not be loaded. No benchmark results are available.
          </p>
        </div>
      </div>
    );
  }

  const configs = snapshot.configurations;
  const researchConfigs: ConfigurationData[] = [
    configs.crs_only,
    configs.transformer_only,
    configs.equal_weight_fusion,
    configs.crs_dominant_fusion,
    configs.ai_dominant_fusion,
  ].filter(Boolean);

  const referenceConfig: ConfigurationData | undefined = configs.reference_live_hybrid;

  const allSelectable: Record<string, ConfigurationData> = {
    ...configs,
  };

  const currentConfig: ConfigurationData =
    allSelectable[selectedKey] || configs.equal_weight_fusion || researchConfigs[0];

  const testCohort = snapshot.cohorts.test_cohort;
  const valCohort = snapshot.cohorts.validation_cohort;

  return (
    <div
      role="region"
      aria-label="Research evaluation report"
      className="flex-grow p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full select-none"
    >
      {/* 1. Navigation Header & Back Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onBack}
            data-testid="research-back-to-overview-btn"
            aria-label="Back to overview"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-panel border border-border text-xs font-medium text-foreground hover:bg-panel-hover hover:border-border-muted transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none mb-3"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-foreground-muted" />
            <span>Back to overview</span>
          </button>

          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-medium text-foreground tracking-tight flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-brand" />
              <span>Research evaluation</span>
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-brand/10 text-brand border border-brand/30">
              Offline Benchmark
            </span>
          </div>
          <p className="text-sm text-foreground-muted mt-1">
            Comparative analysis of rule-based CRS, DistilBERT encoder, and weighted fusion WAF configurations
          </p>
        </div>

        {/* Snapshot Metadata Indicator */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <div
            className="text-xs text-foreground-muted select-none flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-panel/80 border border-border-subtle"
            data-testid="research-snapshot-date"
          >
            <FileText className="w-3.5 h-3.5 text-brand" />
            <span>Saved snapshot:</span>
            <span className="text-foreground font-mono tabular-nums">
              {formatDateTimeIST(snapshot.snapshot_metadata.generated_utc)}
            </span>
          </div>
        </div>
      </div>

      {/* 2. Mandatory Clear Notices */}
      <div className="space-y-3">
        {/* Core Notice */}
        <div
          data-testid="research-disclaimer-notice"
          className="p-4 rounded-lg bg-panel border border-brand/20 flex items-start gap-3 text-xs"
        >
          <Info className="w-4 h-4 text-brand shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-medium text-foreground">
              Exploratory offline results from a curated dataset. These comparisons do not change live website protection.
            </p>
            <p className="text-foreground-muted leading-relaxed">
              <strong>Project-wide benchmark:</strong> These evaluation metrics reflect offline experiments evaluated against fixed curated datasets (118 test requests, 94 valid paired validation samples). They do not represent live traffic from the currently selected customer website. Overview filters (time window, search, verdict filters) do not modify research metrics.
            </p>
          </div>
        </div>

        {/* Synthetic Mode Disclosure (if viewed in synthetic shell) */}
        {isSyntheticMode && (
          <div
            data-testid="research-synthetic-notice"
            className="p-3.5 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-3 text-xs text-amber-200"
          >
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <strong className="text-amber-300">Genuine Offline Research Baseline:</strong> Even though the dashboard shell is currently running in Synthetic Demo mode, this report displays genuine offline benchmark results from historical research artifacts. Synthetic demo requests are never mixed into this measured research data.
            </div>
          </div>
        )}
      </div>

      {/* 3. Cohorts Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Test Cohort */}
        <div className="p-4 rounded-lg bg-panel border border-border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Held-Out Test Cohort
            </span>
            <span className="text-xs font-mono text-brand">
              {testCohort.total_samples} requests
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-1">
            <div className="p-2.5 rounded bg-panel-subtle border border-border-subtle text-center">
              <span className="text-[11px] text-foreground-muted block">Total</span>
              <span className="text-lg font-mono font-semibold text-foreground">
                {testCohort.total_samples}
              </span>
            </div>
            <div className="p-2.5 rounded bg-panel-subtle border border-border-subtle text-center">
              <span className="text-[11px] text-verdict-block block">Attacks</span>
              <span className="text-lg font-mono font-semibold text-verdict-block">
                {testCohort.attack_samples}
              </span>
            </div>
            <div className="p-2.5 rounded bg-panel-subtle border border-border-subtle text-center">
              <span className="text-[11px] text-verdict-allow block">Benign</span>
              <span className="text-lg font-mono font-semibold text-verdict-allow">
                {testCohort.benign_samples}
              </span>
            </div>
          </div>
          <div className="text-[11px] text-foreground-muted flex items-center justify-between border-t border-border-subtle pt-2">
            <span>SQLi: {testCohort.sub_attacks.SQLI} | XSS: {testCohort.sub_attacks.XSS} | Path Traversal: {testCohort.sub_attacks.PATH_TRAVERSAL}</span>
            <span className="text-foreground-dim">{testCohort.inspection_status}</span>
          </div>
        </div>

        {/* Validation Cohort */}
        <div className="p-4 rounded-lg bg-panel border border-border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Validation Tuning Cohort
            </span>
            <span className="text-xs font-mono text-emerald-400">
              {valCohort.valid_paired_samples} / {valCohort.total_samples} valid
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-1">
            <div className="p-2.5 rounded bg-panel-subtle border border-border-subtle text-center">
              <span className="text-[11px] text-foreground-muted block">Total Samples</span>
              <span className="text-lg font-mono font-semibold text-foreground">
                {valCohort.total_samples}
              </span>
            </div>
            <div className="p-2.5 rounded bg-panel-subtle border border-border-subtle text-center">
              <span className="text-[11px] text-emerald-400 block">Valid Paired</span>
              <span className="text-lg font-mono font-semibold text-emerald-400">
                {valCohort.valid_paired_samples}
              </span>
            </div>
            <div className="p-2.5 rounded bg-panel-subtle border border-border-subtle text-center">
              <span className="text-[11px] text-amber-400 block">HTTP 414 Excl.</span>
              <span className="text-lg font-mono font-semibold text-amber-400">
                {valCohort.exclusions_count}
              </span>
            </div>
          </div>
          <div className="text-[11px] text-foreground-muted border-t border-border-subtle pt-2">
            <span>{valCohort.continuation_note}</span>
          </div>
        </div>
      </div>

      {/* 4. Five Architectural Configurations Comparison Table */}
      <div className="p-5 rounded-lg bg-panel border border-border space-y-4">
        <div>
          <h2 className="text-base font-medium text-foreground tracking-tight">
            Five Architectural Configurations (Held-Out Test Set)
          </h2>
          <p className="text-xs text-foreground-muted mt-0.5">
            Evaluated on all 118 test requests. Select any configuration to reveal its confusion matrix and attack-category recall.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table
            className="w-full text-xs text-left border-collapse"
            aria-label="Five WAF configurations test comparison table"
          >
            <thead>
              <tr className="border-b border-border text-foreground-muted font-medium">
                <th scope="col" className="pb-2.5 pr-4">Configuration</th>
                <th scope="col" className="pb-2.5 px-3">Weights (CRS : AI)</th>
                <th scope="col" className="pb-2.5 px-3">Selected θ*</th>
                <th scope="col" className="pb-2.5 px-3 text-right">Accuracy</th>
                <th scope="col" className="pb-2.5 px-3 text-right">Precision</th>
                <th scope="col" className="pb-2.5 px-3 text-right">Recall</th>
                <th scope="col" className="pb-2.5 px-3 text-right">Binary F1</th>
                <th scope="col" className="pb-2.5 pl-3 text-right">Benign FPR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {researchConfigs.map((cfg) => {
                const isSelected = selectedKey === cfg.key;
                const weightsStr =
                  typeof cfg.weights === "object"
                    ? `${cfg.weights.crs.toFixed(2)} : ${cfg.weights.ai.toFixed(2)}`
                    : String(cfg.weights);

                const thresholdStr =
                  typeof cfg.selected_threshold === "number"
                    ? cfg.selected_threshold.toFixed(2)
                    : String(cfg.selected_threshold);

                return (
                  <tr
                    key={cfg.key}
                    tabIndex={0}
                    role="button"
                    aria-selected={isSelected}
                    onClick={() => setSelectedKey(cfg.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedKey(cfg.key);
                      }
                    }}
                    className={`cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset ${
                      isSelected
                        ? "bg-brand/10 text-foreground font-medium"
                        : "hover:bg-panel-hover text-foreground-muted hover:text-foreground"
                    }`}
                  >
                    <td className="py-3 pr-4 font-medium text-foreground flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isSelected ? "bg-brand" : "bg-transparent border border-foreground-muted"
                        }`}
                      />
                      <div>
                        <div>{cfg.name}</div>
                        <div className="text-[11px] text-foreground-dim font-normal">
                          {cfg.role}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 font-mono tabular-nums text-foreground">
                      {weightsStr}
                    </td>
                    <td className="py-3 px-3 font-mono tabular-nums text-foreground">
                      {thresholdStr}
                    </td>
                    <td className="py-3 px-3 text-right font-mono tabular-nums text-foreground">
                      {formatPercent(cfg.metrics.accuracy)}
                    </td>
                    <td className="py-3 px-3 text-right font-mono tabular-nums text-foreground">
                      {formatPercent(cfg.metrics.precision)}
                    </td>
                    <td className="py-3 px-3 text-right font-mono tabular-nums text-foreground">
                      {formatPercent(cfg.metrics.recall)}
                    </td>
                    <td className="py-3 px-3 text-right font-mono tabular-nums text-foreground">
                      {formatPercent(cfg.metrics.f1)}
                    </td>
                    <td className="py-3 pl-3 text-right font-mono tabular-nums text-foreground">
                      {formatPercent(cfg.metrics.benign_fpr)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 5. Reference Live Hybrid Baseline (Displayed Separately) */}
      {referenceConfig && (
        <div
          tabIndex={0}
          role="button"
          aria-selected={selectedKey === referenceConfig.key}
          onClick={() => setSelectedKey(referenceConfig.key)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setSelectedKey(referenceConfig.key);
            }
          }}
          className={`p-5 rounded-lg border transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
            selectedKey === referenceConfig.key
              ? "bg-brand/10 border-brand/60"
              : "bg-panel border-border hover:bg-panel-hover hover:border-border-muted"
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1 max-w-2xl">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
                <h3 className="text-sm font-medium text-foreground">
                  Reference live hybrid — BLOCK-only policy evaluation
                </h3>
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                  Production Reference
                </span>
              </div>
              <p className="text-xs text-foreground-muted leading-relaxed">
                This reference baseline evaluates the production sequential decision policy where only <strong>BLOCK</strong> verdicts are counted as attack detections. Requests marked FLAG or ALLOW count as non-detections. Unlike the five research configurations above, this does not use a continuous score threshold (score ≥ θ*) and is not a combined FLAG-or-BLOCK detection metric.
              </p>
            </div>

            {/* Reference Metrics Grid */}
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <div className="px-3 py-2 rounded bg-panel-subtle border border-border-subtle text-right">
                <span className="text-[10px] text-foreground-muted block">Accuracy</span>
                <span className="text-sm font-mono font-medium text-foreground">
                  {formatPercent(referenceConfig.metrics.accuracy)}
                </span>
              </div>
              <div className="px-3 py-2 rounded bg-panel-subtle border border-border-subtle text-right">
                <span className="text-[10px] text-foreground-muted block">Precision</span>
                <span className="text-sm font-mono font-medium text-foreground">
                  {formatPercent(referenceConfig.metrics.precision)}
                </span>
              </div>
              <div className="px-3 py-2 rounded bg-panel-subtle border border-border-subtle text-right">
                <span className="text-[10px] text-foreground-muted block">Recall</span>
                <span className="text-sm font-mono font-medium text-foreground">
                  {formatPercent(referenceConfig.metrics.recall)}
                </span>
              </div>
              <div className="px-3 py-2 rounded bg-panel-subtle border border-border-subtle text-right">
                <span className="text-[10px] text-foreground-muted block">Binary F1</span>
                <span className="text-sm font-mono font-medium text-foreground">
                  {formatPercent(referenceConfig.metrics.f1)}
                </span>
              </div>
              <div className="px-3 py-2 rounded bg-panel-subtle border border-border-subtle text-right">
                <span className="text-[10px] text-foreground-muted block">Benign FPR</span>
                <span className="text-sm font-mono font-medium text-foreground">
                  {formatPercent(referenceConfig.metrics.benign_fpr)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. Selected Configuration Analysis (Confusion Matrix & Per-Attack Breakdown) */}
      <div className="p-5 rounded-lg bg-panel border border-border space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border pb-3">
          <div>
            <span className="text-xs font-medium text-brand uppercase tracking-wider block">
              Configuration Detail Inspector
            </span>
            <h3 className="text-base font-medium text-foreground mt-0.5">
              {currentConfig.name}
            </h3>
          </div>
          <div className="text-xs text-foreground-muted font-mono">
            {currentConfig.threshold_rule}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Labeled 2x2 Confusion Matrix */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground">
                Confusion Matrix (Total: {currentConfig.metrics.sample_count} requests)
              </span>
              <span className="text-[11px] text-foreground-muted">
                Offline Classification Outcome
              </span>
            </div>

            <div
              className="grid grid-cols-2 gap-2 text-xs"
              aria-label={`Confusion matrix for ${currentConfig.name}`}
            >
              {/* True Positives */}
              <div className="p-3.5 rounded bg-panel-subtle border border-emerald-500/20 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-emerald-400">
                    True Positives (TP)
                  </span>
                  <span className="font-mono text-base font-semibold text-emerald-400">
                    {currentConfig.metrics.tp}
                  </span>
                </div>
                <p className="text-[11px] text-foreground-dim">
                  Attacks correctly classified as malicious
                </p>
              </div>

              {/* False Negatives */}
              <div className="p-3.5 rounded bg-panel-subtle border border-amber-500/20 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-amber-400">
                    False Negatives (FN)
                  </span>
                  <span className="font-mono text-base font-semibold text-amber-400">
                    {currentConfig.metrics.fn}
                  </span>
                </div>
                <p className="text-[11px] text-foreground-dim">
                  Attacks missed (classified as benign)
                </p>
              </div>

              {/* False Positives */}
              <div className="p-3.5 rounded bg-panel-subtle border border-verdict-block/20 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-verdict-block">
                    False Positives (FP)
                  </span>
                  <span className="font-mono text-base font-semibold text-verdict-block">
                    {currentConfig.metrics.fp}
                  </span>
                </div>
                <p className="text-[11px] text-foreground-dim">
                  Benign requests classified as malicious offline (zero live visitor blocks enforced)
                </p>
              </div>

              {/* True Negatives */}
              <div className="p-3.5 rounded bg-panel-subtle border border-border-subtle space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-foreground-muted">
                    True Negatives (TN)
                  </span>
                  <span className="font-mono text-base font-semibold text-foreground">
                    {currentConfig.metrics.tn}
                  </span>
                </div>
                <p className="text-[11px] text-foreground-dim">
                  Benign requests correctly classified as benign
                </p>
              </div>
            </div>
          </div>

          {/* Per-Attack Sub-Category Breakdown (Recall Only) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground">
                Attack Category Breakdown (Recall Only)
              </span>
              <span className="text-[11px] text-foreground-muted">
                91 actual attack samples
              </span>
            </div>

            <div className="space-y-2">
              {/* SQL Injection */}
              <div className="p-3 rounded bg-panel-subtle border border-border-subtle flex items-center justify-between text-xs">
                <div>
                  <span className="font-medium text-foreground block">
                    SQL Injection (SQLi)
                  </span>
                  <span className="text-[11px] text-foreground-muted">
                    {currentConfig.sub_attack_metrics.SQLI.detected_count} detected, {currentConfig.sub_attack_metrics.SQLI.missed_count} missed (n={currentConfig.sub_attack_metrics.SQLI.total_samples})
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-mono font-semibold text-brand">
                    {formatPercent(currentConfig.sub_attack_metrics.SQLI.recall)}
                  </span>
                  <span className="text-[10px] text-foreground-dim block">recall</span>
                </div>
              </div>

              {/* XSS */}
              <div className="p-3 rounded bg-panel-subtle border border-border-subtle flex items-center justify-between text-xs">
                <div>
                  <span className="font-medium text-foreground block">
                    Cross-Site Scripting (XSS)
                  </span>
                  <span className="text-[11px] text-foreground-muted">
                    {currentConfig.sub_attack_metrics.XSS.detected_count} detected, {currentConfig.sub_attack_metrics.XSS.missed_count} missed (n={currentConfig.sub_attack_metrics.XSS.total_samples})
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-mono font-semibold text-brand">
                    {formatPercent(currentConfig.sub_attack_metrics.XSS.recall)}
                  </span>
                  <span className="text-[10px] text-foreground-dim block">recall</span>
                </div>
              </div>

              {/* Path Traversal */}
              <div className="p-3 rounded bg-panel-subtle border border-border-subtle flex items-center justify-between text-xs">
                <div>
                  <span className="font-medium text-foreground block">
                    Path Traversal (LFI/RFI)
                  </span>
                  <span className="text-[11px] text-foreground-muted">
                    {currentConfig.sub_attack_metrics.PATH_TRAVERSAL.detected_count} detected, {currentConfig.sub_attack_metrics.PATH_TRAVERSAL.missed_count} missed (n={currentConfig.sub_attack_metrics.PATH_TRAVERSAL.total_samples})
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-mono font-semibold text-brand">
                    {formatPercent(currentConfig.sub_attack_metrics.PATH_TRAVERSAL.recall)}
                  </span>
                  <span className="text-[10px] text-foreground-dim block">recall</span>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-foreground-dim leading-relaxed pt-1">
              <em>Note: Attack-only subcategories are evaluated strictly for detected count, missed count, and recall. Precision and false-positive rates are mathematically undefined on attack subsets because negative (benign) requests are absent from those subsets.</em>
            </p>
          </div>
        </div>
      </div>

      {/* 7. Collapsible Methodology and Limitations Section */}
      <div className="p-5 rounded-lg bg-panel border border-border space-y-4">
        <button
          type="button"
          onClick={() => setIsMethodologyOpen(!isMethodologyOpen)}
          aria-expanded={isMethodologyOpen}
          aria-controls={methodologyId}
          className="w-full flex items-center justify-between text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded p-1 -m-1"
        >
          <div className="flex items-center gap-2">
            {isMethodologyOpen ? (
              <ChevronDown className="w-4 h-4 text-brand shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-brand shrink-0" />
            )}
            <h2 className="text-base font-medium text-foreground">
              Methodology and limitations
            </h2>
          </div>
          <span className="text-xs text-brand font-medium">
            {isMethodologyOpen ? "Hide details" : "Show details"}
          </span>
        </button>

        {isMethodologyOpen && (
          <div id={methodologyId} className="space-y-5 pt-3 border-t border-border text-xs text-foreground-muted leading-relaxed">
            {/* Score Normalization & Formulas */}
            <div className="space-y-2">
              <h3 className="font-semibold text-foreground text-xs uppercase tracking-wider">
                1. Score Normalization & Weighting Formulas
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>CRS Score Normalization:</strong> C = min(max(s, 0) / 5, 1) — Bounded heuristic severity normalization based on the existing CRS blocking threshold (s ≥ 5). This is a severity scaling, not a calibrated Bayesian probability.
                </li>
                <li>
                  <strong>AI Score:</strong> A = 1 - P(BENIGN) — Computed from the validated 4-class softmax probability vector (sum P = 1.0).
                </li>
                <li>
                  <strong>Weighted Fusion:</strong> F = (w_CRS × C) + (w_AI × A) — Predetermined experimental weights (50:50, 70:30, 30:70); not learned or globally optimal parameters.
                </li>
              </ul>
            </div>

            {/* Threshold Selection & In-Memory Execution */}
            <div className="space-y-2">
              <h3 className="font-semibold text-foreground text-xs uppercase tracking-wider">
                2. In-Memory Threshold Selection on Validation Data
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Tuned strictly on the 94 valid paired validation samples over the grid [0.00, 1.00] step 0.01 (101 points), optimizing binary F1 with ties broken by lower benign FPR, then higher threshold.
                </li>
                <li>
                  <strong>In-Memory Persistence Clarification:</strong> Thresholds were selected in memory immediately prior to test evaluation; no separate pre-test frozen configuration file or artifact was persisted to disk beforehand.
                </li>
              </ul>
            </div>

            {/* Validation HTTP 414 Exclusion & Protocol Continuation */}
            <div className="space-y-2">
              <h3 className="font-semibold text-foreground text-xs uppercase tracking-wider">
                3. Validation Cohort & Transport Exclusion
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Validation evidence collection achieved 94/95 paired successes (98.95% coverage).
                </li>
                <li>
                  Sample <code>patt_xss_L168</code> (XSS) failed with an authentic HTTP 414 (Request-URI Too Long) transport status because its raw query URL (9,070 bytes) exceeded Apache's compiled request-line limit (8,190 bytes).
                </li>
                <li>
                  <strong>Protocol Continuation:</strong> Evaluation continued on the 94 valid paired validation samples despite the original incomplete-collection stop rule.
                </li>
              </ul>
            </div>

            {/* Exploratory Status & Bounded Scope */}
            <div className="space-y-2">
              <h3 className="font-semibold text-foreground text-xs uppercase tracking-wider">
                4. Exploratory Status & Scientific Boundaries
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Exploratory Evaluation:</strong> Conducted on a previously inspected 118-sample held-out test cohort. Full protocol compliance is not claimed.
                </li>
                <li>
                  <strong>No Universal Superiority:</strong> Results reflect specific trade-offs on curated benchmark samples. No configuration is universally superior, and no real-world security guarantees or statistical significance (e.g. bootstrap intervals) are claimed.
                </li>
                <li>
                  <strong>Unmeasured Latency:</strong> Latency benchmarks exist only for the sequential live hybrid; runtime latency for floating-point weighted fusion under production load has not been measured.
                </li>
                <li>
                  <strong>Offline Classification:</strong> False positives represent benign requests classified as malicious offline; these experiments did not enforce visitor blocks in an active proxy.
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
